import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    reviewAssessmentService,
    GetReviewerAssessmentResponse,
    ChapterReview,
    ChapterReviewScoring,
    submissionVersionService,
} from '../services/adminAuth';
import { AlertTriangle, ArrowLeft, CheckCircle, Download, Printer, Save, Send, Upload, X } from 'lucide-react';
import { FILE_BASE_URL } from '../../../api/axios';
import { downloadFileFromUrl } from '../../../utils/downloadHelper';
import { useNotification } from '../../sharedComponents/Notifications';
import { useAuth } from '../../../auth/hooks/auth';

const REVIEW_FORM_PRINT_STYLES = `
@media print {
    @page { size: A4; margin: 14mm 12mm; }
    body, html { margin: 0 !important; padding: 0 !important; background: white !important; }
    aside, nav { display: none !important; }
    .fixed { display: none !important; }
    .w-full.max-w-6xl { max-width: 100% !important; margin: 0 !important; padding: 0 !important; }
    .review-print-doc, .review-print-doc * {
        color: #000 !important;
        background: transparent !important;
        box-shadow: none !important;
    }
    .review-print-doc { background: white !important; }
    .review-print-section { page-break-inside: avoid; break-inside: avoid; }
    .review-print-chapter { page-break-inside: avoid; break-inside: avoid; }
    h1, h2, h3, h4 { break-after: avoid; page-break-after: avoid; }
}
`;

// ── Constants ─────────────────────────────────────────────────────────────────

const ZERO_GUID = '00000000-0000-0000-0000-000000000000';

const CHAPTER_MAX = {
    a: 3, b: 3, c: 3, content: 10, contribution: 5, clarity: 4,
    grammar: 5, style: 5, structure: 5, figures: 5, citations: 5, references: 3,
};
const CHAPTER_TOTAL_MAX = 56;

// ── Helpers ───────────────────────────────────────────────────────────────────

const emptyScoring = (): ChapterReviewScoring => ({
    id: ZERO_GUID, chapterReviewId: ZERO_GUID,
    chapterBalance_a: 0, chapterBalance_b: 0, chapterBalance_c: 0,
    content: 0, contributionScholarship: 0, clarityandConciseness: 0,
    grammarandMechanics: 0, styleandTone: 0, structureandOrganization: 0,
    figuresTablesandIllustrations: 0, citationsandReferences: 0, references: 0, total: 0,
});

const emptyChapter = (num: number): ChapterReview => ({
    id: ZERO_GUID,
    chapterNumber: num,
    chapterBalance: '', chapterLinkage: '', chapterPlacement: '',
    contentAssessment: '', contributionToKnowledge: '', clarity: '',
    grammar: '', style: '', structure: '',
    figuresAssessment: '', figuresRecommendation: '',
    citations: '', references: '',
    chapterReviewScoring: emptyScoring(),
});

// ── Reusable primitives ───────────────────────────────────────────────────────

const ROField = ({ label, value }: { label: string; value?: string }) => (
    <div>
        {label && <p className="text-[10px] font-black text-gray-400 uppercase tracking-wider mb-1">{label}</p>}
        <div className="w-full px-3 py-2 bg-gray-100 border border-gray-200 rounded-md text-sm font-medium text-gray-800 min-h-[36px]">
            {value || <span className="italic text-gray-400">—</span>}
        </div>
    </div>
);

const YesNo = ({ name, value, onChange }: { name: string; value: string; onChange: (v: string) => void }) => (
    <div className="flex gap-6 mt-1">
        {['YES', 'NO'].map(opt => (
            <label key={opt} className="flex items-center gap-1.5 cursor-pointer">
                <input type="radio" name={name} checked={value === opt} onChange={() => onChange(opt)}
                    className="accent-green-700 w-4 h-4" />
                <span className="text-sm font-semibold text-gray-700">{opt}</span>
            </label>
        ))}
    </div>
);

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
    <h3 className="text-xs font-black uppercase tracking-widest text-green-800 border-b border-green-200 pb-2 mb-4">{children}</h3>
);

interface TAreaProps {
    label?: string; value: string; onChange?: (v: string) => void;
    rows?: number; readOnly?: boolean;
}
const TArea = ({ label, value, onChange, rows = 3, readOnly = false }: TAreaProps) => (
    <div>
        {label && <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1">{label}</p>}
        {readOnly ? (
            <div className="w-full px-3 py-2 bg-gray-100 border border-gray-200 rounded-md text-sm text-gray-800 min-h-[60px] whitespace-pre-wrap">
                {value || <span className="italic text-gray-400">—</span>}
            </div>
        ) : (
            <textarea rows={rows} value={value} onChange={e => onChange?.(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-green-600 resize-none" />
        )}
    </div>
);

const ScoreInput = ({ value, onChange, max, readOnly = false }: {
    value: number; onChange: (v: number) => void; max: number; readOnly?: boolean;
}) => (
    readOnly ? (
        <span className="w-14 text-center py-1.5 px-2 border border-gray-200 rounded-md text-sm font-bold bg-gray-100 text-gray-500 inline-block">{value}</span>
    ) : (
        <input type="number" min={0} max={max} value={value === 0 ? '' : value}
            onChange={e => {
                const v = parseInt(e.target.value);
                if (!isNaN(v) && v >= 0 && v <= max) onChange(v);
                else if (e.target.value === '') onChange(0);
            }}
            className="w-14 text-center py-1.5 px-2 border border-gray-300 rounded-md text-sm font-bold outline-none focus:ring-2 focus:ring-green-600 bg-white text-gray-900"
        />
    )
);

// ── Score Badge — shown inline next to a section ──────────────────────────────

interface ScoreBadgeProps {
    label: string;
    value: number;
    onChange: (v: number) => void;
    max: number;
    readOnly?: boolean;
    hint?: string;
}
const ScoreBadge = ({ label, value, onChange, max, readOnly = false, hint }: ScoreBadgeProps) => (
    <div className="flex items-start gap-3 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 mt-3">
        <div className="flex-1 min-w-0">
            <p className="text-[10px] font-black text-emerald-700 uppercase tracking-wider mb-0.5">{label}</p>
            {hint && <p className="text-[10px] text-emerald-600 leading-snug">{hint}</p>}
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
            <span><ScoreInput value={value} onChange={onChange} max={max} readOnly={readOnly} /></span>
            <span className="text-sm font-bold text-emerald-500 uppercase tracking-wider">/ {max}</span>
        </div>
    </div>
);

// ── Confirm Modal ─────────────────────────────────────────────────────────────

interface ConfirmModalProps {
    isOpen: boolean;
    onClose: () => void;
    mode: 'draft' | 'submit';
    onConfirmDraft: () => void;
    onConfirmSubmit: () => void;
    isSavingDraft: boolean;
    isSavingSubmit: boolean;
    manuscriptTitle: string;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
    isOpen, onClose, mode, onConfirmDraft, onConfirmSubmit, isSavingDraft, isSavingSubmit, manuscriptTitle,
}) => {
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/40" onClick={!isSavingDraft && !isSavingSubmit ? onClose : undefined} />
            <div className="relative z-10 w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
                <div className={`px-6 py-5 ${mode === 'submit' ? 'bg-gradient-to-r from-[#088A61] to-[#065f44]' : 'bg-gradient-to-r from-gray-700 to-gray-800'}`}>
                    <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0">
                                {mode === 'submit' ? <Send className="w-5 h-5 text-white" /> : <Save className="w-5 h-5 text-white" />}
                            </div>
                            <div>
                                <h3 className="text-base font-black text-white">
                                    {mode === 'submit' ? 'Submit Manuscript Review' : 'Save as Draft'}
                                </h3>
                                <p className="text-xs text-white/70 mt-0.5">
                                    {mode === 'submit' ? 'This action will finalise your review' : 'Your progress will be saved'}
                                </p>
                            </div>
                        </div>
                        {!isSavingDraft && !isSavingSubmit && (
                            <button onClick={onClose} className="text-white/60 hover:text-white transition-colors flex-shrink-0">
                                <X className="w-5 h-5" />
                            </button>
                        )}
                    </div>
                </div>

                <div className="px-6 py-5 space-y-4">
                    <div className="bg-gray-50 border border-gray-100 rounded-lg px-4 py-3">
                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-wider mb-0.5">Manuscript</p>
                        <p className="text-sm font-semibold text-gray-800 leading-snug line-clamp-2">{manuscriptTitle}</p>
                    </div>

                    {mode === 'submit' ? (
                        <>
                            <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
                                <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                                <p className="text-xs text-amber-700 font-medium leading-relaxed">
                                    Submitting will finalise your review. Make sure all required fields are filled before proceeding.
                                </p>
                            </div>
                            <div className="flex flex-col gap-2 pt-1">
                                <button onClick={onConfirmSubmit} disabled={isSavingSubmit || isSavingDraft}
                                    className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-[#088A61] text-white rounded-xl text-sm font-bold hover:bg-[#067451] transition-colors shadow-md disabled:opacity-60 disabled:cursor-not-allowed">
                                    {isSavingSubmit
                                        ? <><div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />Submitting…</>
                                        : <><Send className="w-4 h-4" />Submit Review</>}
                                </button>
                                <button onClick={onConfirmDraft} disabled={isSavingDraft || isSavingSubmit}
                                    className="w-full flex items-center justify-center gap-2 px-5 py-3 border border-gray-200 text-gray-600 bg-white rounded-xl text-sm font-semibold hover:bg-gray-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed">
                                    {isSavingDraft
                                        ? <><div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />Saving…</>
                                        : <><Save className="w-4 h-4" />Save as Draft Instead</>}
                                </button>
                                <button onClick={onClose} disabled={isSavingDraft || isSavingSubmit}
                                    className="w-full px-5 py-2.5 text-gray-400 text-sm font-medium hover:text-gray-600 transition-colors disabled:opacity-60">
                                    Cancel
                                </button>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="flex items-start gap-3 bg-blue-50 border border-blue-100 rounded-lg px-4 py-3">
                                <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                                <p className="text-xs text-blue-700 font-medium leading-relaxed">
                                    Your form data will be saved as a draft. You can return and complete it at any time before submitting.
                                </p>
                            </div>
                            <div className="flex items-center gap-3 pt-1">
                                <button onClick={onClose} disabled={isSavingDraft}
                                    className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-600 font-semibold hover:bg-gray-50 transition-colors disabled:opacity-60">
                                    Cancel
                                </button>
                                <button onClick={onConfirmDraft} disabled={isSavingDraft}
                                    className="flex-1 flex items-center justify-center gap-2 px-5 py-2.5 bg-gray-800 text-white rounded-xl text-sm font-bold hover:bg-gray-900 transition-colors shadow-sm disabled:opacity-60 disabled:cursor-not-allowed">
                                    {isSavingDraft
                                        ? <><div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />Saving…</>
                                        : <><Save className="w-4 h-4" />Save Draft</>}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

// ── Score pill used in section headers ────────────────────────────────────────
const SectionScore = ({ score, max }: { score: number; max: number }) => (
    <div className="flex items-center gap-1.5 bg-white border border-emerald-200 rounded-full px-3 py-1 shadow-sm">
        <span className="text-xs font-black text-emerald-700">{score}</span>
        <span className="text-[10px] text-emerald-400 font-bold">/ {max}</span>
    </div>
);

// ── Main Component ────────────────────────────────────────────────────────────

const ReviewForm: React.FC = () => {
    const params = useParams();
    const id = params['*'];
    const navigate = useNavigate();
    const { showNotification } = useNotification();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const { user } = useAuth();
    const isReviewer = user?.role === 'accessor';

    const [data, setData] = useState<GetReviewerAssessmentResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [savingDraft, setSavingDraft] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [reviewedManuscriptFile, setReviewedManuscriptFile] = useState<File | null>(null);
    const [confirmModal, setConfirmModal] = useState<'draft' | 'submit' | null>(null);

    // ── Conflict of Interest declaration ──────────────────────────────────────
    const [isConflictModalOpen, setIsConflictModalOpen] = useState(false);
    const [conflictNote, setConflictNote] = useState('');
    const [submittingConflict, setSubmittingConflict] = useState(false);

    const isReadOnly = Boolean(data?.manuscriptReview?.isCompleted && !data?.manuscriptReview?.isDraft);

    // ── Manuscript Identification state ───────────────────────────────────────
    const [conflictOfInterest, setConflictOfInterest] = useState('');
    const [numberOfPages, setNumberOfPages] = useState('');
    const [plagiarismResult, setPlagiarismResult] = useState('');

    // ── Section A state ───────────────────────────────────────────────────────
    const [titleAppropriate, setTitleAppropriate] = useState('');
    const [titleGlobalCoverage, setTitleGlobalCoverage] = useState('');
    const [suggestedTitle, setSuggestedTitle] = useState('');

    // ── Section B state ───────────────────────────────────────────────────────
    const [purposeOfBook, setPurposeOfBook] = useState('');
    const [targetAudienceB2, setTargetAudienceB2] = useState('');
    const [originalitySignificance, setOriginalitySignificance] = useState('');
    const [targetAudienceB4, setTargetAudienceB4] = useState('');
    const [argumentEvidence, setArgumentEvidence] = useState('');
    const [depthComprehensiveness, setDepthComprehensiveness] = useState('');

    // ── Section C state ───────────────────────────────────────────────────────
    const [numChapters, setNumChapters] = useState(1);
    const [chapters, setChapters] = useState<ChapterReview[]>([emptyChapter(1)]);
    const [chapterNarratives, setChapterNarratives] = useState<Array<{
        chapterBalance: string; chapterLink: string; chapterPlacement: string;
        content: string; contribution: string; clarity: string; grammar: string;
        style: string; structure: string;
        figuresTables: string; figuresSpecific: string;
        citationsLink: string; references: string;
    }>>([{
        chapterBalance: '', chapterLink: '', chapterPlacement: '',
        content: '', contribution: '', clarity: '', grammar: '',
        style: '', structure: '',
        figuresTables: '', figuresSpecific: '',
        citationsLink: '', references: '',
    }]);

    // ── Section D state ───────────────────────────────────────────────────────
    const [marketPotential, setMarketPotential] = useState('');
    const [authorCredentials, setAuthorCredentials] = useState('');
    const [pedagogicalFeatures, setPedagogicalFeatures] = useState('');
    const [pedagogicalSuggestions, setPedagogicalSuggestions] = useState('');
    const [competition, setCompetition] = useState('');

    // ── Section E state ───────────────────────────────────────────────────────
    // Single source of truth — these are sent in FormData as General.*
    const [standardComment, setStandardComment] = useState('');
    const [publishable, setPublishable] = useState('');
    const [notPublishableWhy, setNotPublishableWhy] = useState('');
    const [generalObservations, setGeneralObservations] = useState('');
    const [specificObservations, setSpecificObservations] = useState('');
    const [finalRecommendation, setFinalRecommendation] = useState('');

    // ── Score state ───────────────────────────────────────────────────────────
    const [scoresPlagiarism, setScoresPlagiarism] = useState(0);
    const [scoresA1, setScoresA1] = useState(0);
    const [scoresA2, setScoresA2] = useState(0);
    const [scoresB1, setScoresB1] = useState(0);
    const [scoresB2, setScoresB2] = useState(0);
    const [scoresB3, setScoresB3] = useState(0);
    const [scoresB4, setScoresB4] = useState(0);
    const [scoresB5, setScoresB5] = useState(0);
    const [scoresD1, setScoresD1] = useState(0);
    const [scoresD2, setScoresD2] = useState(0);
    const [scoresD3, setScoresD3] = useState(0);
    const [scoresD4, setScoresD4] = useState(0);
    const [scoresD5, setScoresD5] = useState(0);

    // ── Computed ──────────────────────────────────────────────────────────────
    const chapterCSectionScore = useMemo(() => {
        if (chapters.length === 0) return 0;
        const sums = chapters.map(ch => {
            const s = ch.chapterReviewScoring;
            return s.chapterBalance_a + s.chapterBalance_b + s.chapterBalance_c +
                s.content + s.contributionScholarship + s.clarityandConciseness +
                s.grammarandMechanics + s.styleandTone + s.structureandOrganization +
                s.figuresTablesandIllustrations + s.citationsandReferences + s.references;
        });
        return Math.min(Math.round(sums.reduce((a, b) => a + b, 0) / sums.length), CHAPTER_TOTAL_MAX);
    }, [chapters]);

    const grandTotal = useMemo(() => (
        scoresPlagiarism + scoresA1 + scoresA2 +
        scoresB1 + scoresB2 + scoresB3 + scoresB4 + scoresB5 +
        chapterCSectionScore +
        scoresD1 + scoresD2 + scoresD3 + scoresD4 + scoresD5
    ), [scoresPlagiarism, scoresA1, scoresA2, scoresB1, scoresB2, scoresB3, scoresB4, scoresB5,
        chapterCSectionScore, scoresD1, scoresD2, scoresD3, scoresD4, scoresD5]);

    // ── Load ──────────────────────────────────────────────────────────────────
    useEffect(() => {
        const load = async () => {
            if (!id) return;
            setLoading(true);
            try {
                const res = await reviewAssessmentService.getReviewerAssessment(id);
                if (res.isSuccess && res.data) {
                    setData(res.data);
                    prefill(res.data);
                } else {
                    showNotification('error', { title: 'Error', message: res.message || 'Failed to load review assignment' });
                    navigate(-1);
                }
            } catch {
                showNotification('error', { title: 'Error', message: 'An error occurred while loading' });
                navigate(-1);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [id]);

    const prefill = (d: GetReviewerAssessmentResponse) => {
        const mr = d.manuscriptReview;
        const sub = d.reviewAssignment?.submission;

        if (sub?.plagiarismOutcome !== undefined) setPlagiarismResult(`${sub.plagiarismOutcome}%`);
        if (!mr) return;

        setConflictOfInterest(mr.hasConflictOfInterest ? 'YES' : mr.hasConflictOfInterest === false ? 'NO' : '');
        setNumberOfPages(mr.numberOfPages ? String(mr.numberOfPages) : '');
        setPurposeOfBook(mr.purpose ?? '');
        setTargetAudienceB2(mr.targetAudience ?? '');
        setOriginalitySignificance(mr.originalityComment ?? '');
        setArgumentEvidence(mr.argumentAndEvidence ?? '');
        setDepthComprehensiveness(mr.depthAndComprehensiveness ?? '');
        setTitleAppropriate(mr.isTheTitleAppropriate === true ? 'YES' : mr.isTheTitleAppropriate === false ? 'NO' : '');
        setTitleGlobalCoverage(mr.doesTitleGlobalStandard === true ? 'YES' : mr.doesTitleGlobalStandard === false ? 'NO' : '');
        setSuggestedTitle(mr.suggestBetterTitle ?? '');
        setTargetAudienceB4(mr.targetAudienceClearlyDefined ?? '');
        setScoresPlagiarism(mr.score?.plagiarismAIScore ?? 0);

        if (mr.chapters?.length > 0) {
            setNumChapters(mr.chapters.length);
            setChapters(mr.chapters);
            setChapterNarratives(mr.chapters.map(ch => ({
                chapterBalance: ch.chapterBalance ?? '',
                chapterLink: ch.chapterLinkage ?? '',
                chapterPlacement: ch.chapterPlacement ?? '',
                content: ch.contentAssessment ?? '',
                contribution: ch.contributionToKnowledge ?? '',
                clarity: ch.clarity ?? '',
                grammar: ch.grammar ?? '',
                style: ch.style ?? '',
                structure: ch.structure ?? '',
                figuresTables: ch.figuresAssessment ?? '',
                figuresSpecific: ch.figuresRecommendation ?? '',
                citationsLink: ch.citations ?? '',
                references: ch.references ?? '',
            })));
        }

        setMarketPotential(mr.additional?.marketPotential ?? '');
        setAuthorCredentials(mr.additional?.authorCredentials ?? '');
        setPedagogicalFeatures(mr.additional?.pedagogicalFeatures ?? '');
        setPedagogicalSuggestions(mr.additional?.suggestedImprovements ?? '');
        setCompetition(mr.additional?.competitionAnalysis ?? '');

        // Section E — single source of truth, populated from general object
        setStandardComment(mr.general?.standardComment ?? '');
        setGeneralObservations(mr.general?.generalObservation ?? '');
        setSpecificObservations(mr.general?.specificObservation ?? '');
        setFinalRecommendation(mr.general?.finalComment ?? '');
        setNotPublishableWhy(mr.general?.rejectionReason ?? '');

        const recMap: Record<number, string> = {
            1: 'With minor corrections', 2: 'With major corrections',
            3: 'With very major correction', 4: 'Not publishable',
        };
        if (mr.general?.recommendation) {
            setPublishable(recMap[mr.general.recommendation] ?? '');
        }

        const sc = mr.score;
        if (sc) {
            setScoresA1(sc.titleScore ?? 0);
            setScoresA2(sc.coverageScore ?? 0);
            setScoresB1(sc.purposeScore ?? 0);
            setScoresB2(sc.audienceScore ?? 0);
            setScoresB3(sc.originalityScore ?? 0);
            setScoresB4(sc.argumentScore ?? 0);
            setScoresB5(sc.depthScore ?? 0);
            setScoresD1(sc.marketScore ?? 0);
            setScoresD2(sc.authorScore ?? 0);
            setScoresD3(sc.pedagogicalScore ?? 0);
            setScoresD4(sc.competitionScore ?? 0);
            setScoresD5(sc.manuscriptQualityScore ?? 0);
        }
    };

    // ── Chapter management ────────────────────────────────────────────────────
    const updateNumChapters = (count: number) => {
        const c = Math.max(1, Math.min(30, count));
        setNumChapters(c);
        setChapters(prev => {
            const next = [...prev];
            while (next.length < c) next.push(emptyChapter(next.length + 1));
            return next.slice(0, c).map((ch, i) => ({ ...ch, chapterNumber: i + 1 }));
        });
        setChapterNarratives(prev => {
            const next = [...prev];
            while (next.length < c) next.push({
                chapterBalance: '', chapterLink: '', chapterPlacement: '',
                content: '', contribution: '', clarity: '', grammar: '',
                style: '', structure: '',
                figuresTables: '', figuresSpecific: '',
                citationsLink: '', references: '',
            });
            return next.slice(0, c);
        });
    };

    const updateChapterNarrative = (idx: number, field: string, value: string) => {
        setChapterNarratives(prev => prev.map((ch, i) => i === idx ? { ...ch, [field]: value } : ch));
        const narrativeToChapterField: Record<string, keyof ChapterReview> = {
            chapterBalance: 'chapterBalance', chapterLink: 'chapterLinkage',
            chapterPlacement: 'chapterPlacement', content: 'contentAssessment',
            contribution: 'contributionToKnowledge', clarity: 'clarity',
            grammar: 'grammar', style: 'style', structure: 'structure',
            figuresTables: 'figuresAssessment', figuresSpecific: 'figuresRecommendation',
            citationsLink: 'citations', references: 'references',
        };
        const chField = narrativeToChapterField[field];
        if (chField) {
            setChapters(prev => prev.map((ch, i) => i === idx ? { ...ch, [chField]: value } : ch));
        }
    };

    const updateChapterScore = (idx: number, field: keyof ChapterReviewScoring, value: number) =>
        setChapters(prev => prev.map((ch, i) =>
            i === idx ? { ...ch, chapterReviewScoring: { ...ch.chapterReviewScoring, [field]: value } } : ch));

    // ── Conflict of Interest ──────────────────────────────────────────────────
    // YES means a conflict exists → collect a note and record it via ReviewConflict.
    const handleConflictChange = (value: string) => {
        setConflictOfInterest(value);
        if (value === 'YES') {
            setConflictNote('');
            setIsConflictModalOpen(true);
        }
    };

    const closeConflictModal = () => {
        if (submittingConflict) return;
        setIsConflictModalOpen(false);
        // Reset the selection so an undeclared conflict isn't left dangling.
        setConflictOfInterest('');
        setConflictNote('');
    };

    const handleSubmitConflict = async () => {
        const reviewAssignmentId = data?.reviewAssignment?.id;
        if (!reviewAssignmentId || !conflictNote.trim()) return;
        setSubmittingConflict(true);
        try {
            const res = await reviewAssessmentService.reviewConflict({
                reviewAssignmentId,
                conflictNote: conflictNote.trim(),
            });
            if (res.isSuccess) {
                showNotification('success', {
                    title: 'Conflict Declared',
                    message: res.message || 'Your conflict of interest has been recorded. The manuscript will be returned to TETFund.',
                });
                setIsConflictModalOpen(false);
                navigate(-1);
            } else {
                showNotification('error', {
                    title: 'Error',
                    message: res.message || 'Failed to record the conflict of interest.',
                });
            }
        } catch {
            showNotification('error', {
                title: 'Error',
                message: 'An error occurred while recording the conflict of interest.',
            });
        } finally {
            setSubmittingConflict(false);
        }
    };

    // ── Build multipart FormData ──────────────────────────────────────────────
    const buildFormData = (isSubmitted: boolean): FormData => {
        const mr = data?.manuscriptReview;
        const ra = data?.reviewAssignment;
        const sub = ra?.submission;

        const recMap: Record<string, number> = {
            'With minor corrections': 1, 'With major corrections': 2,
            'With very major correction': 3, 'Not publishable': 4,
        };

        const fd = new FormData();

        fd.append('Id', mr?.id ?? ZERO_GUID);
        fd.append('SubmissionId', ra?.submissionId ?? ZERO_GUID);
        fd.append('ReviewAssignmentId', ra?.id ?? ZERO_GUID);
        fd.append('SubmissionVersionId', sub?.currentVersionId ?? ZERO_GUID);
        fd.append('HasConflictOfInterest', String(conflictOfInterest === 'YES'));
        fd.append('IsSubmitted', String(isSubmitted));
        fd.append('Field', cf.category);
        fd.append('NumberOfPages', String(parseInt(numberOfPages) || 0));
        fd.append('Purpose', purposeOfBook);
        fd.append('TargetAudience', targetAudienceB2);
        fd.append('OriginalityComment', originalitySignificance);
        fd.append('ArgumentAndEvidence', argumentEvidence);
        fd.append('DepthAndComprehensiveness', depthComprehensiveness);
        fd.append('SubmittedReviewedManuscriptUrl', mr?.submittedReviewedManuscriptUrl ?? '');
        fd.append('IsTheTitleAppropriate', String(titleAppropriate === 'YES'));
        fd.append('DoesTitleGlobalStandard', String(titleGlobalCoverage === 'YES'));
        fd.append('SuggestBetterTitle', titleAppropriate === 'NO' ? suggestedTitle : '');
        fd.append('TargetAudienceClearlyDefined', targetAudienceB4);

        if (reviewedManuscriptFile) {
            fd.append('ReviewedManuscriptFile', reviewedManuscriptFile);
        }

        chapters.forEach((chapter, i) => {
            fd.append(`Chapters[${i}][Id]`, chapter.id ?? ZERO_GUID);
            fd.append(`Chapters[${i}][ChapterNumber]`, String(chapter.chapterNumber));
            fd.append(`Chapters[${i}][ChapterBalance]`, chapter.chapterBalance ?? '');
            fd.append(`Chapters[${i}][ChapterLinkage]`, chapter.chapterLinkage ?? '');
            fd.append(`Chapters[${i}][ChapterPlacement]`, chapter.chapterPlacement ?? '');
            fd.append(`Chapters[${i}][ContentAssessment]`, chapter.contentAssessment ?? '');
            fd.append(`Chapters[${i}][ContributionToKnowledge]`, chapter.contributionToKnowledge ?? '');
            fd.append(`Chapters[${i}][Clarity]`, chapter.clarity ?? '');
            fd.append(`Chapters[${i}][Grammar]`, chapter.grammar ?? '');
            fd.append(`Chapters[${i}][Style]`, chapter.style ?? '');
            fd.append(`Chapters[${i}][Structure]`, chapter.structure ?? '');
            fd.append(`Chapters[${i}][FiguresAssessment]`, chapter.figuresAssessment ?? '');
            fd.append(`Chapters[${i}][FiguresRecommendation]`, chapter.figuresRecommendation ?? '');
            fd.append(`Chapters[${i}][Citations]`, chapter.citations ?? '');
            fd.append(`Chapters[${i}][References]`, chapter.references ?? '');

            const scoring = chapter.chapterReviewScoring;
            if (scoring) {
                fd.append(`Chapters[${i}][ChapterReviewScoring][Id]`, scoring.id ?? ZERO_GUID);
                fd.append(`Chapters[${i}][ChapterReviewScoring][ChapterReviewId]`, scoring.chapterReviewId ?? ZERO_GUID);
                fd.append(`Chapters[${i}][ChapterReviewScoring][ChapterBalance_a]`, String(scoring.chapterBalance_a ?? 0));
                fd.append(`Chapters[${i}][ChapterReviewScoring][ChapterBalance_b]`, String(scoring.chapterBalance_b ?? 0));
                fd.append(`Chapters[${i}][ChapterReviewScoring][ChapterBalance_c]`, String(scoring.chapterBalance_c ?? 0));
                fd.append(`Chapters[${i}][ChapterReviewScoring][Content]`, String(scoring.content ?? 0));
                fd.append(`Chapters[${i}][ChapterReviewScoring][ContributionScholarship]`, String(scoring.contributionScholarship ?? 0));
                fd.append(`Chapters[${i}][ChapterReviewScoring][ClarityandConciseness]`, String(scoring.clarityandConciseness ?? 0));
                fd.append(`Chapters[${i}][ChapterReviewScoring][GrammarandMechanics]`, String(scoring.grammarandMechanics ?? 0));
                fd.append(`Chapters[${i}][ChapterReviewScoring][StyleandTone]`, String(scoring.styleandTone ?? 0));
                fd.append(`Chapters[${i}][ChapterReviewScoring][StructureandOrganization]`, String(scoring.structureandOrganization ?? 0));
                fd.append(`Chapters[${i}][ChapterReviewScoring][FiguresTablesandIllustrations]`, String(scoring.figuresTablesandIllustrations ?? 0));
                fd.append(`Chapters[${i}][ChapterReviewScoring][CitationsandReferences]`, String(scoring.citationsandReferences ?? 0));
                fd.append(`Chapters[${i}][ChapterReviewScoring][References]`, String(scoring.references ?? 0));
                fd.append(`Chapters[${i}][ChapterReviewScoring][Total]`, String(scoring.total ?? 0));
            }
        });

        fd.append('Additional.MarketPotential', marketPotential);
        fd.append('Additional.AuthorCredentials', authorCredentials);
        fd.append('Additional.PedagogicalFeatures', pedagogicalFeatures);
        fd.append('Additional.SuggestedImprovements', pedagogicalSuggestions);
        fd.append('Additional.CompetitionAnalysis', competition);

        // Section E — using the single source of truth states
        fd.append('General.StandardComment', standardComment);
        fd.append('General.GeneralObservation', generalObservations);
        fd.append('General.SpecificObservation', specificObservations);
        fd.append('General.Recommendation', String(recMap[publishable] ?? 1));
        fd.append('General.RejectionReason', notPublishableWhy);
        fd.append('General.FinalComment', finalRecommendation);

        fd.append('Score.TitleScore', String(scoresA1));
        fd.append('Score.PlagiarismAIScore', String(scoresPlagiarism));
        fd.append('Score.CoverageScore', String(scoresA2));
        fd.append('Score.PurposeScore', String(scoresB1));
        fd.append('Score.AudienceScore', String(scoresB2));
        fd.append('Score.OriginalityScore', String(scoresB3));
        fd.append('Score.ArgumentScore', String(scoresB4));
        fd.append('Score.DepthScore', String(scoresB5));
        fd.append('Score.ChapterScore', String(chapterCSectionScore));
        fd.append('Score.MarketScore', String(scoresD1));
        fd.append('Score.AuthorScore', String(scoresD2));
        fd.append('Score.PedagogicalScore', String(scoresD3));
        fd.append('Score.CompetitionScore', String(scoresD4));
        fd.append('Score.ManuscriptQualityScore', String(scoresD5));
        fd.append('Score.TotalScore', String(grandTotal));

        return fd;
    };

    // ── Save / Submit ─────────────────────────────────────────────────────────
    const handleSaveDraft = async () => {
        setSavingDraft(true);
        await new Promise(r => setTimeout(r, 0));
        try {
            const res = await reviewAssessmentService.submitReviewAssessmentFormData(buildFormData(false));
            if (res?.isSuccess === true) {
                showNotification('success', { title: 'Draft Saved', message: 'Your review has been saved as a draft.' });
                setConfirmModal(null);
                if (id) {
                    const reload = await reviewAssessmentService.getReviewerAssessment(id);
                    if (reload.isSuccess && reload.data) { setData(reload.data); prefill(reload.data); }
                }
            } else {
                showNotification('error', { title: 'Error', message: res?.message || 'Failed to save draft.' });
            }
        } catch {
            showNotification('error', { title: 'Error', message: 'An unexpected error occurred.' });
        } finally {
            setSavingDraft(false);
        }
    };

    const handleSubmit = async () => {
        const hasExistingUpload = Boolean(data?.manuscriptReview?.submittedReviewedManuscriptUrl);
        if (!reviewedManuscriptFile && !hasExistingUpload) {
            showNotification('error', {
                title: 'Upload Required',
                message: 'Please upload your reviewed manuscript before submitting.',
            });
            return;
        }
        setSubmitting(true);
        await new Promise(r => setTimeout(r, 0));
        try {
            const res = await reviewAssessmentService.submitReviewAssessmentFormData(buildFormData(true));
            if (res?.isSuccess === true) {
                showNotification('success', { title: 'Submitted', message: 'Review submitted successfully.' });
                setConfirmModal(null);
                navigate(-1);
            } else {
                showNotification('error', { title: 'Submission Failed', message: res?.message || 'Failed to submit review.' });
            }
        } catch {
            showNotification('error', { title: 'Error', message: 'An unexpected error occurred.' });
        } finally {
            setSubmitting(false);
        }
    };

    // ── Download ──────────────────────────────────────────────────────────────
    // const handleDownload = async () => {
    //     const sub = data?.reviewAssignment?.submission;
    //     const url = sub?.versions?.slice(-1)[0]?.manuscriptUrl;
    //     if (!url) return;
    //     setIsDownloading(true);
    //     try {
    //         const fullUrl = url.startsWith('http') ? url : `${FILE_BASE_URL}/${url}`;
    //         await downloadFileFromUrl(fullUrl, `Manuscript_${sub?.submissionNumber}.pdf`);
    //     } catch {
    //         showNotification('error', { title: 'Error', message: 'Failed to download manuscript' });
    //     } finally {
    //         setIsDownloading(false);
    //     }
    // };

    const handleDownload = async () => {
        const submissionId = data?.reviewAssignment?.submission?.id;
        if (!submissionId) return;
        setIsDownloading(true);
        try {
            const res = await submissionVersionService.getUploadedVersionsBySubmissionId(submissionId);
            if (!res.isSuccess || !res.data?.length) {
                showNotification('error', { title: 'Error', message: 'No manuscript versions found.' });
                return;
            }
            // Sort by versionNumber descending, take latest
            const latest = [...res.data].sort((a, b) => b.versionNumber - a.versionNumber)[0];
            const url = latest.manuscriptUrl.startsWith('http')
                ? latest.manuscriptUrl
                : `${FILE_BASE_URL}/${latest.manuscriptUrl}`;
            await downloadFileFromUrl(url, `Manuscript_${data?.reviewAssignment?.submission?.submissionNumber}_v${latest.versionNumber}.docx`);
        } catch {
            showNotification('error', { title: 'Error', message: 'Failed to download manuscript.' });
        } finally {
            setIsDownloading(false);
        }
    };

    // ── File upload validation ────────────────────────────────────────────────
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] ?? null;
        if (!file) return;
        const allowed = ['.doc', '.docx', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
        const ext = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
        if (!allowed.includes(ext) && !allowed.includes(file.type)) {
            showNotification('error', { title: 'Invalid File', message: 'Only .doc and .docx files are accepted.' });
            e.target.value = '';
            return;
        }
        setReviewedManuscriptFile(file);
        // reset input so same file can be re-selected after removal
        e.target.value = '';
    };

    // ── Loading ───────────────────────────────────────────────────────────────
    if (loading) return (
        <div className="flex h-96 items-center justify-center">
            <div className="w-10 h-10 border-4 border-gray-100 border-t-[#088A61] rounded-full animate-spin" />
        </div>
    );

    const ra = data?.reviewAssignment;
    const sub = ra?.submission;
    const cf = {
        title: sub?.title ?? '—',
        submissionNumber: sub?.submissionNumber ?? '—',
        category: sub?.category?.name ?? '—',
        applicationType: sub?.applicationType === 1 ? 'Book' : 'Journal Article',
    };

    // Partial totals for live score display
    const sectionAScore = scoresA1 + scoresA2;
    const sectionBScore = scoresB1 + scoresB2 + scoresB3 + scoresB4 + scoresB5;
    const sectionDScore = scoresD1 + scoresD2 + scoresD3 + scoresD4 + scoresD5;

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div className="w-full max-w-6xl mx-auto px-4 py-6 pb-24">
            <style>{REVIEW_FORM_PRINT_STYLES}</style>

            <ConfirmModal
                isOpen={confirmModal !== null}
                onClose={() => !savingDraft && !submitting && setConfirmModal(null)}
                mode={confirmModal ?? 'draft'}
                onConfirmDraft={handleSaveDraft}
                onConfirmSubmit={handleSubmit}
                isSavingDraft={savingDraft}
                isSavingSubmit={submitting}
                manuscriptTitle={cf.title}
            />

            {/* ── Conflict of Interest Declaration Modal ── */}
            {isConflictModalOpen && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 print:hidden">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md relative overflow-hidden">
                        <div className="px-6 py-4 border-b border-gray-100 flex items-start justify-between gap-3">
                            <div className="flex items-start gap-2.5">
                                {/* <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" /> */}
                                <div>
                                    <h3 className="text-base font-bold text-gray-900">Declare Conflict of Interest</h3>
                                    <p className="text-xs text-gray-500 mt-0.5">
                                        State the nature of the conflict. The manuscript will be returned to TETFund.
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={closeConflictModal}
                                disabled={submittingConflict}
                                className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400 disabled:opacity-50"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="px-6 py-5">
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                Conflict Note <span className="text-red-500">*</span>
                            </label>
                            <textarea
                                value={conflictNote}
                                onChange={(e) => setConflictNote(e.target.value)}
                                placeholder="Describe the conflict of interest..."
                                rows={4}
                                autoFocus
                                className="block w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                            />
                        </div>

                        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-end gap-3">
                            <button
                                onClick={closeConflictModal}
                                disabled={submittingConflict}
                                className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 font-medium hover:bg-gray-100 transition-colors disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSubmitConflict}
                                disabled={submittingConflict || !conflictNote.trim()}
                                className="px-5 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                                {submittingConflict ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                        Submitting...
                                    </>
                                ) : (
                                    'Submit Conflict'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="print:hidden">
            {/* Back */}
            <div className="mb-4">
                <button onClick={() => navigate(-1)}
                    className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800 font-medium">
                    <ArrowLeft className="w-4 h-4" /> Back
                </button>
            </div>

            {/* Header */}
            <div className="mb-6 flex flex-col sm:flex-row gap-3 items-start sm:items-center">
                <div className="flex-1">
                    <h1 className="text-2xl font-black text-gray-900 leading-tight">Manuscript Review Form</h1>
                    <p className="text-sm text-gray-500">Submission ID: {cf.submissionNumber}</p>
                    {isReadOnly && (
                        <span className="inline-flex items-center mt-1 px-2.5 py-1 rounded-md text-sm font-semibold bg-green-50 text-green-700 border border-green-200">
                            This Review has been Submitted and is now Read Only
                        </span>
                    )}
                </div>
                <div className="flex gap-2">
                    <button onClick={() => window.print()}
                        className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
                        <Printer className="w-4 h-4" /> Print
                    </button>
                    <button onClick={handleDownload} disabled={isDownloading}
                        className="flex items-center gap-2 px-4 py-2 bg-[#088A61] text-white rounded-lg text-sm font-semibold hover:bg-[#067451] disabled:opacity-50">
                        {isDownloading
                            ? <><div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />Downloading...</>
                            : <><Download className="w-4 h-4" />Download Manuscript</>}
                    </button>
                </div>
            </div>

            {/* TETFund Header */}
            <div className="bg-[#088A61] text-white rounded-t-2xl px-6 py-4 text-center">
                <p className="text-[10px] font-bold uppercase tracking-widest opacity-75 mb-1">Tertiary Education Trust Fund</p>
                <h2 className="text-lg font-black">MANUSCRIPT EVALUATION CRITERIA</h2>
                <p className="text-xs opacity-75 mt-1">Note: This is a guide and should be word-processed. Additional reports as attachments are highly welcomed.</p>
            </div>

            {/* Scoring legend strip */}
            <div className="bg-emerald-700 text-white px-6 py-2 flex flex-wrap items-center gap-x-6 gap-y-1">
                <span className="text-[10px] font-black uppercase tracking-widest opacity-80">Current Total Score</span>
                <div className="flex items-baseline gap-1.5">
                    <span className={`text-2xl font-black ${grandTotal >= 60 ? 'text-white' : 'text-red-300'}`}>{grandTotal}</span>
                    <span className="text-xs opacity-70 font-bold">/ 100</span>
                </div>
                <span className="text-[10px] opacity-60">|</span>
                <span className="text-[10px] opacity-75 font-semibold">Pass mark: 60%</span>
                {grandTotal >= 60
                    ? <span className="ml-auto text-[10px] font-black bg-white/20 rounded-full px-2.5 py-0.5">PASS</span>
                    : <span className="ml-auto text-[10px] font-black bg-red-500/40 rounded-full px-2.5 py-0.5">BELOW PASS MARK</span>
                }
            </div>

            <div id="printContentForm" className="bg-white rounded-b-2xl shadow-sm border border-gray-100 border-t-0 p-4 sm:p-6 space-y-6">             

                {/* ── Manuscript Identification ── */}
                <div className="rounded-xl border border-gray-200 p-5 space-y-4">
                    <SectionTitle>Manuscript Identification</SectionTitle>
                    <ROField label="Title of Manuscript" value={cf.title} />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <ROField label="Manuscript Number" value={cf.submissionNumber} />
                        <ROField label="Manuscript's Area / Field" value={cf.category} />
                        {!isReviewer && <ROField label="Name of (Lead) Author" value={sub?.author?.person?.fullName} />}
                        <ROField label="Application Type" value={cf.applicationType} />
                        <ROField label="Reviewer" value={ra?.reviewer?.person?.fullName} />
                        <ROField label="Assigned By" value={ra?.assignedByUser?.person?.fullName} />
                    </div>
                    {!isReviewer && <ROField label="Other Authors (provide only 3 where multiple names apply)" value={sub?.authors} />}
                    {/* ── Assessor Information ── */}
                    <div className="rounded-xl border border-gray-200 p-5 space-y-4">
                        <SectionTitle>Assessor Information</SectionTitle>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {[
                                { label: 'Name of Assessor', val: ra?.reviewer?.person?.fullName},
                                { label: "Assessor's Field of Study", val: cf.category},
                                { label: 'Best Contact Number', val: ra?.reviewer?.person?.phoneNo},
                            ].map(({ label, val }) => (
                                <div key={label}>
                                    <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1">{label}</p>
                                    <ROField label="" value={val} />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* ── Conflict of Interest ── */}
                <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                    <p className="text-sm font-bold text-red-800 mb-2">Any Conflict of Interest?</p>
                    {isReadOnly
                        ? <div className="flex gap-6">{['YES', 'NO'].map(opt => (
                            <label key={opt} className="flex items-center gap-1.5 opacity-60 cursor-default">
                                <input type="radio" readOnly checked={conflictOfInterest === opt} className="accent-green-700 w-4 h-4" />
                                <span className="text-sm font-semibold text-gray-700">{opt}</span>
                            </label>
                        ))}</div>
                        : <YesNo name="conflictA" value={conflictOfInterest} onChange={handleConflictChange} />
                    }
                    <p className="text-xs text-red-600 mt-2 font-medium">If YES, decline and return to TETFund. If NO, continue with the objective review.</p>
                </div>

                {/* Plagiarism — narrative + score together */}
                <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4 space-y-3">
                    <p className="text-[10px] font-black text-amber-700 uppercase tracking-wider">Plagiarism &amp; AI Check</p>
                    <ROField label="State Plagiarism Test Results (Do not review if above 50%)" value={plagiarismResult} />
                    
                    <ScoreBadge
                        label="Score: Plagiarism &amp; AI Results"
                        value={scoresPlagiarism}
                        onChange={setScoresPlagiarism}
                        max={2}
                        readOnly={isReadOnly}
                        hint="Max 2 pts — assess both plagiarism percentage and AI-generation risk"
                    />
                </div>

                {/* ── Section A: Title ── */}
                <div className="rounded-xl border border-gray-200 p-5 space-y-4">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        <SectionTitle>A. Title</SectionTitle>
                        <SectionScore score={sectionAScore} max={7} />
                    </div>

                    {/* 1. Number of Pages */}
                    <div>
                        <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                            1. Number of Pages (Recommended ≥ 250)
                        </p>
                        {isReadOnly
                            ? <ROField label="" value={numberOfPages} />
                            : <input
                                type="number"
                                value={numberOfPages}
                                onChange={e => setNumberOfPages(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-600"
                            />
                        }
                    </div>

                    {/* 2. Is the title appropriate? */}
                    <div>
                        <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-2">
                            2. Is the title appropriate?
                        </p>
                        {isReadOnly
                            ? <div className="flex gap-6">{['YES', 'NO'].map(opt => (
                                <label key={opt} className="flex items-center gap-1.5 opacity-60 cursor-default">
                                    <input type="radio" readOnly checked={titleAppropriate === opt} className="accent-green-700 w-4 h-4" />
                                    <span className="text-sm font-semibold text-gray-700">{opt}</span>
                                </label>
                            ))}</div>
                            : <YesNo name="titleAppropriate" value={titleAppropriate} onChange={setTitleAppropriate} />
                        }
                        <ScoreBadge label="Score: Title Appropriateness" value={scoresA1} onChange={setScoresA1} max={5} readOnly={isReadOnly} hint="Max 5 pts" />
                    </div>

                    {/* 3. Conditional on title appropriate answer */}
                    {titleAppropriate === 'NO' && (
                        <TArea
                            label="3. Suggest a Better Title (where Necessary)"
                            value={suggestedTitle}
                            onChange={setSuggestedTitle}
                            rows={2}
                            readOnly={isReadOnly}
                        />
                    )}

                    <div>
                        <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-2">
                            {titleAppropriate === 'NO' ? '4.' : '3.'} Does the Title have an Acceptable Global/Regional/Discipline Coverage?
                        </p>
                        {isReadOnly
                            ? <div className="flex gap-6">{['YES', 'NO'].map(opt => (
                                <label key={opt} className="flex items-center gap-1.5 opacity-60 cursor-default">
                                    <input type="radio" readOnly checked={titleGlobalCoverage === opt} className="accent-green-700 w-4 h-4" />
                                    <span className="text-sm font-semibold text-gray-700">{opt}</span>
                                </label>
                            ))}</div>
                            : <YesNo name="titleGlobalCoverage" value={titleGlobalCoverage} onChange={setTitleGlobalCoverage} />
                        }
                        <ScoreBadge label="Score: Global / Regional Coverage" value={scoresA2} onChange={setScoresA2} max={2} readOnly={isReadOnly} hint="Max 2 pts" />
                    </div>
                </div>

                {/* ── Section B: Overview ── */}
                <div className="rounded-xl border border-gray-200 p-5 space-y-5">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        <SectionTitle>B. Overview of the Content and Structure</SectionTitle>
                        <SectionScore score={sectionBScore} max={21} />
                    </div>

                    {[
                        { num: '1', label: 'Purpose of the Book: Briefly state the primary purpose and objectives', val: purposeOfBook, set: setPurposeOfBook, scoreVal: scoresB1, scoreSet: setScoresB1, max: 5, scoreLabel: 'Score: Purpose of Book' },
                        { num: '2', label: 'Target Audience: Identify the intended audience within tertiary institutions (e.g., undergraduate, graduate, researchers, educators)', val: targetAudienceB2, set: setTargetAudienceB2, scoreVal: scoresB2, scoreSet: setScoresB2, max: 4, scoreLabel: 'Score: Target Audience' },
                        { num: '3', label: 'Originality and Significance: Does the book address a new/important topic? Does it offer a fresh perspective? Comment after responding.', val: originalitySignificance, set: setOriginalitySignificance, scoreVal: scoresB3, scoreSet: setScoresB3, max: 4, scoreLabel: 'Score: Originality & Significance' },
                        { num: '4', label: 'Target Audience: Is the target audience clearly defined? Is the content appropriate for the intended audience? Comment after responding.', val: targetAudienceB4, set: setTargetAudienceB4, scoreVal: null, scoreSet: null, max: 0, scoreLabel: '' },
                        { num: '5', label: 'Argument and Evidence: Is the central argument clearly stated and well-supported? Is the evidence credible and up-to-date? Comment after responding.', val: argumentEvidence, set: setArgumentEvidence, scoreVal: scoresB4, scoreSet: setScoresB4, max: 4, scoreLabel: 'Score: Argument & Evidence' },
                        { num: '6', label: 'Depth and Comprehensiveness: Does the book provide a thorough and balanced treatment? Are all relevant aspects considered? Comment after responding.', val: depthComprehensiveness, set: setDepthComprehensiveness, scoreVal: scoresB5, scoreSet: setScoresB5, max: 4, scoreLabel: 'Score: Depth & Comprehensiveness' },
                    ].map(item => (
                        <div key={item.num} className="space-y-2">
                            <TArea label={`${item.num}. ${item.label}`} value={item.val} onChange={item.set} rows={3} readOnly={isReadOnly} />
                            {item.scoreVal !== null && item.scoreSet && (
                                <ScoreBadge label={item.scoreLabel} value={item.scoreVal} onChange={item.scoreSet} max={item.max} readOnly={isReadOnly} hint={`Max ${item.max} pts`} />
                            )}
                        </div>
                    ))}
                </div>

                {/* ── Section C: Chapter by Chapter ── */}
                <div className="rounded-xl border border-gray-200 p-5 space-y-5">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                        <div className="flex items-center justify-between flex-wrap gap-2 w-full">
                            <SectionTitle>C. Chapter by Chapter Assessment</SectionTitle>
                            <SectionScore score={chapterCSectionScore} max={56} />
                        </div>
                    </div>
                    <p className="text-xs text-gray-500 italic -mt-3">
                        In your review, fill this section for every chapter. You MUST reproduce Section C by its number of chapters.
                        <br />
                        <span className="font-semibold text-emerald-700">Chapter scores are averaged across all chapters and capped at 56.</span>
                    </p>

                    {!isReadOnly && (
                        <div className="flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-lg px-4 py-2.5">
                            <span className="text-xs font-bold text-gray-500">Number of Chapters:</span>
                            <input type="number" min={1} max={30} value={numChapters}
                                onChange={e => updateNumChapters(parseInt(e.target.value) || 1)}
                                className="w-16 text-center py-1 px-2 border border-gray-300 rounded-md text-sm font-bold focus:outline-none focus:ring-2 focus:ring-green-600" />
                        </div>
                    )}

                    {chapterNarratives.map((ch, idx) => {
                        const scoring = chapters[idx]?.chapterReviewScoring ?? emptyScoring();
                        return (
                            <div key={idx} className="border border-emerald-200 rounded-xl overflow-hidden">
                                {/* Chapter header */}
                                <div className="bg-emerald-700 text-white px-4 py-2.5 flex items-center justify-between">
                                    <span className="text-sm font-black uppercase tracking-wider">Chapter {idx + 1}</span>
                                    <div className="flex items-center gap-1.5 bg-white/20 rounded-full px-3 py-0.5">
                                        <span className="text-xs font-black">{
                                            scoring.chapterBalance_a + scoring.chapterBalance_b + scoring.chapterBalance_c +
                                            scoring.content + scoring.contributionScholarship + scoring.clarityandConciseness +
                                            scoring.grammarandMechanics + scoring.styleandTone + scoring.structureandOrganization +
                                            scoring.figuresTablesandIllustrations + scoring.citationsandReferences + scoring.references
                                        }</span>
                                        <span className="text-[10px] opacity-70">/ {CHAPTER_TOTAL_MAX}</span>
                                    </div>
                                </div>

                                <div className="p-4 bg-emerald-50/20 space-y-4">

                                    {/* 1. Chapter Balance */}
                                    <div className="space-y-2">
                                        <p className="text-[11px] font-black text-emerald-800 uppercase tracking-wider">1. Chapter Balance</p>
                                        <TArea label="a. Is there a balance between the length and depth of this chapter?" value={ch.chapterBalance} onChange={v => updateChapterNarrative(idx, 'chapterBalance', v)} rows={2} readOnly={isReadOnly} />
                                        <TArea label="b. Is the current chapter having good link with both preceding and proceeding one?" value={ch.chapterLink} onChange={v => updateChapterNarrative(idx, 'chapterLink', v)} rows={2} readOnly={isReadOnly} />
                                        <TArea label="c. Is the chapter appropriately placed or does it need relocation? (make suggestion where necessary)" value={ch.chapterPlacement} onChange={v => updateChapterNarrative(idx, 'chapterPlacement', v)} rows={2} readOnly={isReadOnly} />
                                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                                            <ScoreBadge label="a. Balance (length/depth)" value={scoring.chapterBalance_a} onChange={v => updateChapterScore(idx, 'chapterBalance_a', v)} max={CHAPTER_MAX.a} readOnly={isReadOnly} />
                                            <ScoreBadge label="b. Balance (linkage)" value={scoring.chapterBalance_b} onChange={v => updateChapterScore(idx, 'chapterBalance_b', v)} max={CHAPTER_MAX.b} readOnly={isReadOnly} />
                                            <ScoreBadge label="c. Balance (placement)" value={scoring.chapterBalance_c} onChange={v => updateChapterScore(idx, 'chapterBalance_c', v)} max={CHAPTER_MAX.c} readOnly={isReadOnly} />
                                        </div>
                                    </div>

                                    {/* 2–9 individual items with inline scores */}
                                    {[
                                        { num: '2', label: 'Content: Comment in detail on the scope and content of the manuscript chapter and its adequacy to meet the need of target users.', narField: 'content', scoreField: 'content' as keyof ChapterReviewScoring, scoreVal: scoring.content, max: CHAPTER_MAX.content, scoreLabel: 'Content Score' },
                                        { num: '3', label: 'Contribution to Scholarship: Does it contribute/add significant value to existing knowledge? State the contribution.', narField: 'contribution', scoreField: 'contributionScholarship' as keyof ChapterReviewScoring, scoreVal: scoring.contributionScholarship, max: CHAPTER_MAX.contribution, scoreLabel: 'Contribution Score' },
                                        { num: '4', label: 'Clarity and Conciseness: Is the chapter writing clear, concise, and easy to understand? Is technical jargon explained?', narField: 'clarity', scoreField: 'clarityandConciseness' as keyof ChapterReviewScoring, scoreVal: scoring.clarityandConciseness, max: CHAPTER_MAX.clarity, scoreLabel: 'Clarity Score' },
                                        { num: '5', label: 'Grammar and Mechanics: Is the manuscript free of grammatical errors and typos?', narField: 'grammar', scoreField: 'grammarandMechanics' as keyof ChapterReviewScoring, scoreVal: scoring.grammarandMechanics, max: CHAPTER_MAX.grammar, scoreLabel: 'Grammar Score' },
                                        { num: '6', label: 'Style and Tone: Is the writing style engaging and appropriate for the topic and audience?', narField: 'style', scoreField: 'styleandTone' as keyof ChapterReviewScoring, scoreVal: scoring.styleandTone, max: CHAPTER_MAX.style, scoreLabel: 'Style Score' },
                                        { num: '7', label: 'Structure and Organization: Is the book well-organized with a logical flow? Does each chapter have a clear focus and contribute to the overall argument? (Provide its contribution/s)', narField: 'structure', scoreField: 'structureandOrganization' as keyof ChapterReviewScoring, scoreVal: scoring.structureandOrganization, max: CHAPTER_MAX.structure, scoreLabel: 'Structure Score' },
                                    ].map(item => (
                                        <div key={item.num} className="space-y-2">
                                            <TArea label={`${item.num}. ${item.label}`} value={(ch as any)[item.narField]} onChange={v => updateChapterNarrative(idx, item.narField, v)} rows={item.narField === 'content' ? 4 : 2} readOnly={isReadOnly} />
                                            <ScoreBadge label={item.scoreLabel} value={item.scoreVal as number} onChange={v => updateChapterScore(idx, item.scoreField, v)} max={item.max} readOnly={isReadOnly} hint={`Max ${item.max} pts`} />
                                        </div>
                                    ))}

                                    {/* 8. Figures */}
                                    <div className="space-y-2">
                                        <TArea label="8a. Figures, Tables, and Illustrations: Are all figures, tables, and illustrations relevant and clearly presented? Do they have clear captions and labels?" value={ch.figuresTables} onChange={v => updateChapterNarrative(idx, 'figuresTables', v)} rows={2} readOnly={isReadOnly} />
                                        <TArea label="8b. Be Specific: Which figures, tables, and/or illustrations need to be provided/adjusted/removed/reproduced?" value={ch.figuresSpecific} onChange={v => updateChapterNarrative(idx, 'figuresSpecific', v)} rows={2} readOnly={isReadOnly} />
                                        <ScoreBadge label="Figures, Tables & Illustrations Score" value={scoring.figuresTablesandIllustrations} onChange={v => updateChapterScore(idx, 'figuresTablesandIllustrations', v)} max={CHAPTER_MAX.figures} readOnly={isReadOnly} hint={`Max ${CHAPTER_MAX.figures} pts`} />
                                    </div>

                                    {/* 9. Citations */}
                                    <div className="space-y-2">
                                        <TArea label="9a. Citations and References: Is this chapter well linked to the references/bibliography?" value={ch.citationsLink} onChange={v => updateChapterNarrative(idx, 'citationsLink', v)} rows={2} readOnly={isReadOnly} />
                                        <ScoreBadge label="Citations & References Score" value={scoring.citationsandReferences} onChange={v => updateChapterScore(idx, 'citationsandReferences', v)} max={CHAPTER_MAX.citations} readOnly={isReadOnly} hint={`Max ${CHAPTER_MAX.citations} pts`} />
                                        <TArea label="9b. References: Are all sources cited correctly and consistently? Is the reference list complete and accurate? (Be Specific: Provide information on wrong/expected/poorly cited/bad/weak/too local/necessary local/cardinal references)" value={ch.references} onChange={v => updateChapterNarrative(idx, 'references', v)} rows={2} readOnly={isReadOnly} />
                                        <ScoreBadge label="References Quality Score" value={scoring.references} onChange={v => updateChapterScore(idx, 'references', v)} max={CHAPTER_MAX.references} readOnly={isReadOnly} hint={`Max ${CHAPTER_MAX.references} pts`} />
                                    </div>
                                </div>
                            </div>
                        );
                    })}

                    {/* Section C averaged score display */}
                    <div className="flex items-center justify-between bg-emerald-700 text-white rounded-xl px-5 py-3">
                        <div>
                            <p className="text-[10px] font-black uppercase tracking-wider opacity-75">Section C — Averaged Score</p>
                            <p className="text-xs opacity-60 mt-0.5">(sum of all chapter scores ÷ number of chapters, capped at 56)</p>
                        </div>
                        <div className="flex items-baseline gap-1.5">
                            <span className="text-3xl font-black">{chapterCSectionScore}</span>
                            <span className="text-sm opacity-70 font-bold">/ 56</span>
                        </div>
                    </div>

                    <p className="text-xs text-center text-emerald-700 font-bold italic">*** PLEASE DO NOT FORGET TO REPEAT THE ABOVE SECTION 'C' FOR EVERY CHAPTER ***</p>
                </div>

                {/* ── Section D: Additional ── */}
                <div className="rounded-xl border border-gray-200 p-5 space-y-5">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        <SectionTitle>D. Additional Considerations</SectionTitle>
                        <SectionScore score={sectionDScore} max={14} />
                    </div>

                    {[
                        { num: '1', label: 'Market Potential: Is there a market for this book? Who are the potential buyers? List them.', val: marketPotential, set: setMarketPotential, scoreVal: scoresD1, scoreSet: setScoresD1, max: 3, scoreLabel: 'Score: Market Potential' },
                        { num: '2', label: "Author's Credentials: Do the authors have the necessary expertise to write this book? Provide your known, online, observable information.", val: authorCredentials, set: setAuthorCredentials, scoreVal: scoresD2, scoreSet: setScoresD2, max: 3, scoreLabel: "Score: Author's Credentials" },
                    ].map(item => (
                        <div key={item.num} className="space-y-2">
                            <TArea label={`${item.num}. ${item.label}`} value={item.val} onChange={item.set} rows={3} readOnly={isReadOnly} />
                            <ScoreBadge label={item.scoreLabel} value={item.scoreVal} onChange={item.scoreSet} max={item.max} readOnly={isReadOnly} hint={`Max ${item.max} pts`} />
                        </div>
                    ))}

                    <div className="space-y-2">
                        <TArea label="3a. Pedagogical Features: Does the book include helpful features for students, such as learning objectives, key terms, discussion questions, or suggested readings?" value={pedagogicalFeatures} onChange={setPedagogicalFeatures} rows={2} readOnly={isReadOnly} />
                        <TArea label="3b. Tell us which one of these you will suggest that must still be added and how?" value={pedagogicalSuggestions} onChange={setPedagogicalSuggestions} rows={2} readOnly={isReadOnly} />
                        <ScoreBadge label="Score: Pedagogical Features" value={scoresD3} onChange={setScoresD3} max={3} readOnly={isReadOnly} hint="Max 3 pts" />
                    </div>

                    <div className="space-y-2">
                        <TArea label="4. Competition: How does this book compare to existing books on the same topic?" value={competition} onChange={setCompetition} rows={3} readOnly={isReadOnly} />
                        <ScoreBadge label="Score: Competition Analysis" value={scoresD4} onChange={setScoresD4} max={3} readOnly={isReadOnly} hint="Max 3 pts" />
                    </div>

                    {/* D5 — manuscript quality with guidance */}
                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-2">
                        <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">5. Manuscript Quality Score</p>
                        <p className="text-xs text-gray-500 leading-relaxed">Score based on double-line spacing page count:<br />
                            <span className="font-semibold">½</span> for &lt;251 pages &nbsp;·&nbsp;
                            <span className="font-semibold">1</span> for 251–350 pages &nbsp;·&nbsp;
                            <span className="font-semibold">1½</span> for 351–450 pages &nbsp;·&nbsp;
                            <span className="font-semibold">2</span> for &gt;450 pages
                        </p>
                        <ScoreBadge label="Score: Manuscript Quality" value={scoresD5} onChange={setScoresD5} max={2} readOnly={isReadOnly} hint="Max 2 pts" />
                    </div>
                </div>

                {/* ── Section E: General Comments ── */}
                <div className="rounded-xl border border-gray-200 p-5 space-y-4">
                    <SectionTitle>E. General Comments</SectionTitle>

                    <TArea label="1. Standard: Comment on the quality of the manuscript" value={standardComment} onChange={setStandardComment} rows={3} readOnly={isReadOnly} />

                    <div>
                        <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-2">2. Is it appropriate to have the manuscript published as a book for tertiary education?</p>
                        {isReadOnly ? <ROField label="" value={publishable} /> : (
                            <div className="space-y-2">
                                {['With minor corrections', 'With major corrections', 'With very major correction', 'Not publishable'].map(opt => (
                                    <label key={opt} className="flex items-center gap-2 cursor-pointer">
                                        <input type="radio" name="publishableA" checked={publishable === opt} onChange={() => setPublishable(opt)} className="accent-green-700 w-4 h-4" />
                                        <span className="text-sm text-gray-700 font-medium">{opt}</span>
                                    </label>
                                ))}
                            </div>
                        )}
                        {publishable === 'Not publishable' && (
                            <div className="mt-3">
                                <TArea label="Not publishable — why?" value={notPublishableWhy} onChange={setNotPublishableWhy} rows={2} readOnly={isReadOnly} />
                            </div>
                        )}
                    </div>

                    <TArea label="3. General observations (in detail)" value={generalObservations} onChange={setGeneralObservations} rows={4} readOnly={isReadOnly} />
                    <TArea label="4. Specific observations (in detail): comment on style, format, acknowledgement, dedication, foreword, table of content, lists, printing style, pagination, general arrangement, heading style, chapter division, etc." value={specificObservations} onChange={setSpecificObservations} rows={4} readOnly={isReadOnly} />
                    <TArea label="5. Final Recommendation and Total Score (specific, clear, not cloudy and unambiguous; the general score for the manuscript should be reflected here — Note: 60% is the Pass Mark)" value={finalRecommendation} onChange={setFinalRecommendation} rows={3} readOnly={isReadOnly} />
                </div>

                {/* ── Reviewed manuscript upload ── */}
                {!isReadOnly && (
                    <div className="rounded-xl border border-gray-200 p-5 space-y-3">
                        <SectionTitle>Upload Reviewed Manuscript <span className="text-red-500">*</span></SectionTitle>
                        <p className="text-xs text-gray-500">
                            Upload your annotated/reviewed copy of the manuscript. Only <strong>.doc</strong> and <strong>.docx</strong> files are accepted. Required to submit.
                        </p>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                            className="hidden"
                            onChange={handleFileChange}
                        />

                        {/* Show previously saved upload if exists and no new file chosen */}
                        {!reviewedManuscriptFile && data?.manuscriptReview?.submittedReviewedManuscriptUrl && (
                            <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
                                <CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs font-semibold text-blue-700">Previously uploaded document (saved draft)</p>
                                    <p className="text-xs text-blue-500 truncate mt-0.5">
                                        {data.manuscriptReview.submittedReviewedManuscriptUrl.split('/').pop()}
                                    </p>
                                </div>
                                <button
                                    onClick={() => fileInputRef.current?.click()}
                                    className="text-xs font-semibold text-blue-600 hover:text-blue-800 whitespace-nowrap">
                                    Replace
                                </button>
                            </div>
                        )}

                        {/* New file chosen */}
                        {reviewedManuscriptFile ? (
                            <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-lg px-4 py-3">
                                <Upload className="w-4 h-4 text-green-600 flex-shrink-0" />
                                <span className="text-sm font-medium text-green-800 flex-1 truncate">{reviewedManuscriptFile.name}</span>
                                <button
                                    onClick={() => {
                                        setReviewedManuscriptFile(null);
                                        if (fileInputRef.current) fileInputRef.current.value = '';
                                    }}
                                    className="text-green-600 hover:text-red-500 transition-colors">
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        ) : !data?.manuscriptReview?.submittedReviewedManuscriptUrl && (
                            <button onClick={() => fileInputRef.current?.click()}
                                className="flex items-center gap-2 px-4 py-2 border border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition-colors">
                                <Upload className="w-4 h-4" />
                                Choose file (DOC, DOCX only)
                            </button>
                        )}

                        {/* Show upload button when there's an existing upload but no new file picked yet */}
                        {!reviewedManuscriptFile && data?.manuscriptReview?.submittedReviewedManuscriptUrl && (
                            <button onClick={() => fileInputRef.current?.click()}
                                className="flex items-center gap-2 px-4 py-2 border border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition-colors">
                                <Upload className="w-4 h-4" />
                                Upload a different file
                            </button>
                        )}
                    </div>
                )}

                {/* ── Grand Total Summary Card ── */}
                <div className={`rounded-xl border-2 p-5 ${grandTotal >= 60 ? 'border-emerald-300 bg-emerald-50' : 'border-red-200 bg-red-50'}`}>
                    <p className={`text-[10px] font-black uppercase tracking-widest mb-3 ${grandTotal >= 60 ? 'text-emerald-700' : 'text-red-700'}`}>Score Summary</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-4">
                        {[
                            { label: 'Plagiarism & AI', score: scoresPlagiarism, max: 2 },
                            { label: 'Section A — Title', score: sectionAScore, max: 7 },
                            { label: 'Section B — Overview', score: sectionBScore, max: 21 },
                            { label: 'Section C — Chapters', score: chapterCSectionScore, max: 56 },
                            { label: 'Section D — Additional', score: sectionDScore, max: 14 },
                        ].map(item => (
                            <div key={item.label} className="bg-white rounded-lg border border-gray-200 px-3 py-2.5 text-center shadow-sm">
                                <p className="text-[9px] font-black text-gray-400 uppercase tracking-wider mb-1 leading-tight">{item.label}</p>
                                <p className="text-lg font-black text-gray-800">{item.score}<span className="text-xs font-bold text-gray-400"> /{item.max}</span></p>
                            </div>
                        ))}
                    </div>
                    <div className={`flex items-center justify-between rounded-xl px-5 py-4 ${grandTotal >= 60 ? 'bg-emerald-700' : 'bg-red-600'} text-white`}>
                        <div>
                            <p className="text-[10px] font-black uppercase tracking-widest opacity-75">Grand Total</p>
                            <p className="text-xs opacity-60 mt-0.5">Pass mark: 60 / 100</p>
                        </div>
                        <div className="flex items-baseline gap-2">
                            <span className="text-4xl font-black">{grandTotal}</span>
                            <span className="text-lg opacity-70 font-bold">/ 100</span>
                            <span className={`ml-2 text-sm font-black px-3 py-1 rounded-full ${grandTotal >= 60 ? 'bg-white/25' : 'bg-white/20'}`}>
                                {grandTotal >= 60 ? 'PASS' : 'FAIL'}
                            </span>
                        </div>
                    </div>
                </div>      

                {isReadOnly && data?.manuscriptReview?.submittedReviewedManuscriptUrl && (
                    <div className="rounded-xl border border-gray-200 p-5">
                        <SectionTitle>Reviewer's Submitted Manuscript</SectionTitle>
                        <p className="text-xs text-gray-500 mb-3">
                            The reviewer uploaded an annotated copy of the manuscript alongside this review.
                        </p>
                        <button
                            onClick={async () => {
                                const url = data.manuscriptReview!.submittedReviewedManuscriptUrl;
                                const fullUrl = url.startsWith('http') ? url : `${FILE_BASE_URL}/${url}`;
                                try {
                                    await downloadFileFromUrl(
                                        fullUrl,
                                        `Reviewed_Manuscript_${data?.reviewAssignment?.submission?.submissionNumber}.docx`
                                    );
                                } catch {
                                    showNotification('error', { title: 'Error', message: 'Failed to download reviewed manuscript.' });
                                }
                            }}
                            className="flex items-center gap-2 px-5 py-3 bg-[#088A61] text-white rounded-lg text-sm font-semibold hover:bg-[#067451] transition-colors">
                            <Download className="w-4 h-4" />
                            Download Reviewed Manuscript
                        </button>
                    </div>
                )}         

            </div>

            {/* Sticky footer */}
            {!isReadOnly && (
                <div className="fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-gray-100 px-4 sm:px-6 py-3 flex items-center justify-between gap-3 shadow-lg">
                    <div className="flex items-center gap-2">
                        <span className="text-[10px] font-black text-gray-400 uppercase tracking-wider hidden sm:block">Total</span>
                        <span className={`text-lg font-black ${grandTotal >= 60 ? 'text-emerald-700' : 'text-red-500'}`}>{grandTotal}</span>
                        <span className="text-xs text-gray-400 font-bold">/ 100</span>
                    </div>
                    <div className="flex gap-2">
                        <button onClick={() => navigate(-1)} disabled={savingDraft || submitting}
                            className="px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 font-medium transition-colors disabled:opacity-50">
                            Cancel
                        </button>
                        <button onClick={() => setConfirmModal('draft')} disabled={savingDraft || submitting}
                            className="flex items-center gap-2 px-4 py-2 border border-[#088A61] text-[#088A61] bg-white rounded-lg text-sm font-bold hover:bg-emerald-50 transition-colors disabled:opacity-50">
                            <Save className="w-4 h-4" /><span className="hidden sm:inline">Save</span> Draft
                        </button>
                        <button onClick={() => setConfirmModal('submit')} disabled={savingDraft || submitting}
                            className="flex items-center gap-2 px-4 sm:px-6 py-2 bg-[#088A61] text-white rounded-lg text-sm font-bold hover:bg-[#067451] transition-colors shadow-md disabled:opacity-50">
                            <Send className="w-4 h-4" /><span className="hidden sm:inline">Submit</span> Review
                        </button>
                    </div>
                </div>
            )}
            </div>

            <ReviewFormPrintView
                cf={cf}
                sub={sub}
                ra={ra}
                isReviewer={isReviewer}
                isReadOnly={isReadOnly}
                conflictOfInterest={conflictOfInterest}
                plagiarismResult={plagiarismResult}
                scoresPlagiarism={scoresPlagiarism}
                numberOfPages={numberOfPages}
                titleAppropriate={titleAppropriate}
                suggestedTitle={suggestedTitle}
                titleGlobalCoverage={titleGlobalCoverage}
                scoresA1={scoresA1}
                scoresA2={scoresA2}
                sectionAScore={sectionAScore}
                purposeOfBook={purposeOfBook}
                targetAudienceB2={targetAudienceB2}
                originalitySignificance={originalitySignificance}
                targetAudienceB4={targetAudienceB4}
                argumentEvidence={argumentEvidence}
                depthComprehensiveness={depthComprehensiveness}
                scoresB1={scoresB1}
                scoresB2={scoresB2}
                scoresB3={scoresB3}
                scoresB4={scoresB4}
                scoresB5={scoresB5}
                sectionBScore={sectionBScore}
                numChapters={numChapters}
                chapters={chapters}
                chapterNarratives={chapterNarratives}
                chapterCSectionScore={chapterCSectionScore}
                marketPotential={marketPotential}
                authorCredentials={authorCredentials}
                pedagogicalFeatures={pedagogicalFeatures}
                pedagogicalSuggestions={pedagogicalSuggestions}
                competition={competition}
                scoresD1={scoresD1}
                scoresD2={scoresD2}
                scoresD3={scoresD3}
                scoresD4={scoresD4}
                scoresD5={scoresD5}
                sectionDScore={sectionDScore}
                standardComment={standardComment}
                publishable={publishable}
                notPublishableWhy={notPublishableWhy}
                generalObservations={generalObservations}
                specificObservations={specificObservations}
                finalRecommendation={finalRecommendation}
                grandTotal={grandTotal}
                reviewedManuscriptUrl={data?.manuscriptReview?.submittedReviewedManuscriptUrl}
            />
        </div>
    );
};

// ── Print-only view ──────────────────────────────────────────────────────────
// Built from the same state values that drive the on-screen form. Hidden on
// screen (display:none via Tailwind `hidden`) and shown only during `@media print`.

interface ReviewFormPrintViewProps {
    cf: { title: string; submissionNumber: string; category: string; applicationType: string };
    sub: any;
    ra: any;
    isReviewer: boolean;
    isReadOnly: boolean;
    conflictOfInterest: string;
    plagiarismResult: string;
    scoresPlagiarism: number;
    numberOfPages: string;
    titleAppropriate: string;
    suggestedTitle: string;
    titleGlobalCoverage: string;
    scoresA1: number;
    scoresA2: number;
    sectionAScore: number;
    purposeOfBook: string;
    targetAudienceB2: string;
    originalitySignificance: string;
    targetAudienceB4: string;
    argumentEvidence: string;
    depthComprehensiveness: string;
    scoresB1: number;
    scoresB2: number;
    scoresB3: number;
    scoresB4: number;
    scoresB5: number;
    sectionBScore: number;
    numChapters: number;
    chapters: ChapterReview[];
    chapterNarratives: Array<{
        chapterBalance: string; chapterLink: string; chapterPlacement: string;
        content: string; contribution: string; clarity: string; grammar: string;
        style: string; structure: string;
        figuresTables: string; figuresSpecific: string;
        citationsLink: string; references: string;
    }>;
    chapterCSectionScore: number;
    marketPotential: string;
    authorCredentials: string;
    pedagogicalFeatures: string;
    pedagogicalSuggestions: string;
    competition: string;
    scoresD1: number;
    scoresD2: number;
    scoresD3: number;
    scoresD4: number;
    scoresD5: number;
    sectionDScore: number;
    standardComment: string;
    publishable: string;
    notPublishableWhy: string;
    generalObservations: string;
    specificObservations: string;
    finalRecommendation: string;
    grandTotal: number;
    reviewedManuscriptUrl?: string;
}

const PrintField = ({ label, value }: { label: string; value?: string }) => (
    <div style={{ marginBottom: '6pt' }}>
        <div style={{ fontSize: '8pt', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
        <div style={{ fontSize: '9.5pt', whiteSpace: 'pre-wrap', borderBottom: '0.5pt solid #555', paddingBottom: '2pt', minHeight: '14pt' }}>
            {value && value.trim() ? value : '—'}
        </div>
    </div>
);

const PrintScoreLine = ({ label, value, max }: { label: string; value: number; max: number }) => (
    <div style={{ fontSize: '9pt', marginBottom: '4pt' }}>
        <strong>{label}:</strong> {value} / {max}
    </div>
);

const ReviewFormPrintView: React.FC<ReviewFormPrintViewProps> = (p) => {
    const sectionHeader = (label: string, score?: { value: number; max: number; suffix?: string }) => (
        <h3 style={{ fontSize: '10.5pt', fontWeight: 800, textTransform: 'uppercase', borderBottom: '1pt solid #000', paddingBottom: '3pt', marginBottom: '8pt', display: 'flex', justifyContent: 'space-between' }}>
            <span>{label}</span>
            {score && <span style={{ fontSize: '9.5pt', fontWeight: 700 }}>{score.value} / {score.max}{score.suffix ?? ''}</span>}
        </h3>
    );

    return (
        <div className="hidden print:block review-print-doc" style={{ fontFamily: 'Georgia, "Times New Roman", serif', color: '#000', background: 'white', fontSize: '10pt', lineHeight: 1.4 }}>

            {/* ── HEADER ── */}
            <div className="review-print-section" style={{ borderBottom: '2pt solid #000', paddingBottom: '8pt', marginBottom: '12pt', textAlign: 'center' }}>
                <p style={{ fontSize: '9pt', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700, margin: 0 }}>
                    Tertiary Education Trust Fund
                </p>
                <h1 style={{ fontSize: '18pt', fontWeight: 800, margin: '4pt 0 2pt 0', lineHeight: 1.1 }}>
                    Manuscript Review Form
                </h1>
                <p style={{ fontSize: '10pt', margin: '0 0 4pt 0', fontStyle: 'italic' }}>Manuscript Evaluation Criteria</p>
                <p style={{ fontSize: '9.5pt', marginTop: '4pt', marginBottom: 0 }}>
                    <strong>Submission ID:</strong> {p.cf.submissionNumber}
                </p>
            </div>

            {/* ── SUBMISSION DETAILS ── */}
            <div className="review-print-section" style={{ marginBottom: '14pt' }}>
                {sectionHeader('Submission Details')}
                <table style={{ width: '100%', fontSize: '9.5pt', borderCollapse: 'collapse' }}>
                    <tbody>
                        <tr><td style={{ fontWeight: 700, padding: '2pt 6pt 2pt 0', verticalAlign: 'top', width: '32%' }}>Title of Manuscript</td><td style={{ padding: '2pt 0' }}>{p.cf.title}</td></tr>
                        <tr><td style={{ fontWeight: 700, padding: '2pt 6pt 2pt 0', verticalAlign: 'top' }}>Manuscript Number</td><td style={{ padding: '2pt 0' }}>{p.cf.submissionNumber}</td></tr>
                        <tr><td style={{ fontWeight: 700, padding: '2pt 6pt 2pt 0', verticalAlign: 'top' }}>Thematic Area</td><td style={{ padding: '2pt 0' }}>{p.cf.category}</td></tr>
                        <tr><td style={{ fontWeight: 700, padding: '2pt 6pt 2pt 0', verticalAlign: 'top' }}>Application Type</td><td style={{ padding: '2pt 0' }}>{p.cf.applicationType}</td></tr>
                        {!p.isReviewer && (
                            <tr><td style={{ fontWeight: 700, padding: '2pt 6pt 2pt 0', verticalAlign: 'top' }}>Lead Author</td><td style={{ padding: '2pt 0' }}>{p.sub?.author?.person?.fullName || '—'}</td></tr>
                        )}
                        {!p.isReviewer && p.sub?.authors && (
                            <tr><td style={{ fontWeight: 700, padding: '2pt 6pt 2pt 0', verticalAlign: 'top' }}>Other Authors</td><td style={{ padding: '2pt 0' }}>{p.sub.authors}</td></tr>
                        )}
                        <tr><td style={{ fontWeight: 700, padding: '2pt 6pt 2pt 0', verticalAlign: 'top' }}>Reviewer / Assessor</td><td style={{ padding: '2pt 0' }}>{p.ra?.reviewer?.person?.fullName || '—'}</td></tr>
                        <tr><td style={{ fontWeight: 700, padding: '2pt 6pt 2pt 0', verticalAlign: 'top' }}>Assessor's Field of Study</td><td style={{ padding: '2pt 0' }}>{p.cf.category}</td></tr>
                        <tr><td style={{ fontWeight: 700, padding: '2pt 6pt 2pt 0', verticalAlign: 'top' }}>Best Contact Number</td><td style={{ padding: '2pt 0' }}>{p.ra?.reviewer?.person?.phoneNo || '—'}</td></tr>
                        <tr><td style={{ fontWeight: 700, padding: '2pt 6pt 2pt 0', verticalAlign: 'top' }}>Assigned By</td><td style={{ padding: '2pt 0' }}>{p.ra?.assignedByUser?.person?.fullName || '—'}</td></tr>
                    </tbody>
                </table>
            </div>

            {/* ── SCORE SUMMARY ── */}
            <div className="review-print-section" style={{ marginBottom: '14pt', border: '1pt solid #000', padding: '8pt 10pt' }}>
                {sectionHeader('Score Summary')}
                <table style={{ width: '100%', fontSize: '9.5pt', borderCollapse: 'collapse' }}>
                    <tbody>
                        <tr><td style={{ padding: '2pt 0' }}>Plagiarism &amp; AI</td><td style={{ textAlign: 'right', padding: '2pt 0' }}>{p.scoresPlagiarism} / 2</td></tr>
                        <tr><td style={{ padding: '2pt 0' }}>Section A — Title</td><td style={{ textAlign: 'right', padding: '2pt 0' }}>{p.sectionAScore} / 7</td></tr>
                        <tr><td style={{ padding: '2pt 0' }}>Section B — Overview</td><td style={{ textAlign: 'right', padding: '2pt 0' }}>{p.sectionBScore} / 21</td></tr>
                        <tr><td style={{ padding: '2pt 0' }}>Section C — Chapters (averaged)</td><td style={{ textAlign: 'right', padding: '2pt 0' }}>{p.chapterCSectionScore} / 56</td></tr>
                        <tr><td style={{ padding: '2pt 0' }}>Section D — Additional</td><td style={{ textAlign: 'right', padding: '2pt 0' }}>{p.sectionDScore} / 14</td></tr>
                        <tr style={{ borderTop: '1pt solid #000' }}>
                            <td style={{ padding: '4pt 0 0 0', fontWeight: 800, fontSize: '11pt' }}>GRAND TOTAL ({p.grandTotal >= 60 ? 'PASS' : 'FAIL'})</td>
                            <td style={{ textAlign: 'right', padding: '4pt 0 0 0', fontWeight: 800, fontSize: '13pt' }}>{p.grandTotal} / 100</td>
                        </tr>
                    </tbody>
                </table>
                <p style={{ fontSize: '8pt', marginTop: '3pt', fontStyle: 'italic' }}>Pass mark: 60 / 100.</p>
            </div>

            {/* ── CONFLICT OF INTEREST ── */}
            <div className="review-print-section" style={{ marginBottom: '10pt', border: '0.75pt solid #000', padding: '6pt 8pt' }}>
                <p style={{ fontSize: '9.5pt', margin: 0 }}><strong>Conflict of Interest:</strong> {p.conflictOfInterest || '—'}</p>
                <p style={{ fontSize: '8pt', margin: '2pt 0 0 0', fontStyle: 'italic' }}>If YES, decline and return to TETFund. If NO, continue with the objective review.</p>
            </div>

            {/* ── PLAGIARISM ── */}
            <div className="review-print-section" style={{ marginBottom: '12pt' }}>
                {sectionHeader('Plagiarism & AI Check', { value: p.scoresPlagiarism, max: 2 })}
                <PrintField label="Plagiarism Test Result (do not review if above 50%)" value={p.plagiarismResult} />
                <PrintScoreLine label="Score: Plagiarism & AI Results" value={p.scoresPlagiarism} max={2} />
            </div>

            {/* ── SECTION A ── */}
            <div className="review-print-section" style={{ marginBottom: '12pt' }}>
                {sectionHeader('A. Title', { value: p.sectionAScore, max: 7 })}
                <PrintField label="1. Number of Pages (recommended ≥ 250)" value={p.numberOfPages} />
                <PrintField label="2. Is the title appropriate?" value={p.titleAppropriate} />
                <PrintScoreLine label="Score: Title Appropriateness" value={p.scoresA1} max={5} />
                {p.titleAppropriate === 'NO' && (
                    <PrintField label="3. Suggested Better Title" value={p.suggestedTitle} />
                )}
                <PrintField
                    label={`${p.titleAppropriate === 'NO' ? '4.' : '3.'} Does the Title have an Acceptable Global / Regional / Discipline Coverage?`}
                    value={p.titleGlobalCoverage}
                />
                <PrintScoreLine label="Score: Global / Regional Coverage" value={p.scoresA2} max={2} />
            </div>

            {/* ── SECTION B ── */}
            <div className="review-print-section" style={{ marginBottom: '12pt' }}>
                {sectionHeader('B. Overview of the Content and Structure', { value: p.sectionBScore, max: 21 })}
                <PrintField label="1. Purpose of the Book (primary purpose and objectives)" value={p.purposeOfBook} />
                <PrintScoreLine label="Score: Purpose of Book" value={p.scoresB1} max={5} />
                <PrintField label="2. Target Audience (within tertiary institutions)" value={p.targetAudienceB2} />
                <PrintScoreLine label="Score: Target Audience" value={p.scoresB2} max={4} />
                <PrintField label="3. Originality & Significance" value={p.originalitySignificance} />
                <PrintScoreLine label="Score: Originality & Significance" value={p.scoresB3} max={4} />
                <PrintField label="4. Target Audience clearly defined? Is content appropriate for them?" value={p.targetAudienceB4} />
                <PrintField label="5. Argument & Evidence" value={p.argumentEvidence} />
                <PrintScoreLine label="Score: Argument & Evidence" value={p.scoresB4} max={4} />
                <PrintField label="6. Depth & Comprehensiveness" value={p.depthComprehensiveness} />
                <PrintScoreLine label="Score: Depth & Comprehensiveness" value={p.scoresB5} max={4} />
            </div>

            {/* ── SECTION C ── */}
            <div style={{ marginBottom: '12pt' }}>
                {sectionHeader('C. Chapter-by-Chapter Assessment', { value: p.chapterCSectionScore, max: 56, suffix: ' (averaged)' })}
                <p style={{ fontSize: '8pt', fontStyle: 'italic', margin: '0 0 6pt 0' }}>Total chapters: {p.numChapters}. Each chapter is scored out of 56; the section score is the average across all chapters.</p>
                {p.chapterNarratives.map((ch, idx) => {
                    const sc = p.chapters[idx]?.chapterReviewScoring ?? emptyScoring();
                    const chapterTotal =
                        sc.chapterBalance_a + sc.chapterBalance_b + sc.chapterBalance_c +
                        sc.content + sc.contributionScholarship + sc.clarityandConciseness +
                        sc.grammarandMechanics + sc.styleandTone + sc.structureandOrganization +
                        sc.figuresTablesandIllustrations + sc.citationsandReferences + sc.references;
                    return (
                        <div key={idx} className="review-print-chapter" style={{ border: '0.75pt solid #000', padding: '6pt 8pt', marginBottom: '8pt' }}>
                            <h4 style={{ fontSize: '10pt', fontWeight: 800, textTransform: 'uppercase', margin: '0 0 6pt 0', display: 'flex', justifyContent: 'space-between' }}>
                                <span>Chapter {idx + 1}</span>
                                <span style={{ fontSize: '9pt' }}>{chapterTotal} / {CHAPTER_TOTAL_MAX}</span>
                            </h4>
                            <PrintField label="1a. Chapter Balance — length / depth" value={ch.chapterBalance} />
                            <PrintScoreLine label="Score: 1a Balance (length / depth)" value={sc.chapterBalance_a} max={CHAPTER_MAX.a} />
                            <PrintField label="1b. Chapter Linkage (with preceding and proceeding chapter)" value={ch.chapterLink} />
                            <PrintScoreLine label="Score: 1b Balance (linkage)" value={sc.chapterBalance_b} max={CHAPTER_MAX.b} />
                            <PrintField label="1c. Chapter Placement (relocation suggestion if needed)" value={ch.chapterPlacement} />
                            <PrintScoreLine label="Score: 1c Balance (placement)" value={sc.chapterBalance_c} max={CHAPTER_MAX.c} />
                            <PrintField label="2. Content (scope, content, adequacy)" value={ch.content} />
                            <PrintScoreLine label="Score: Content" value={sc.content} max={CHAPTER_MAX.content} />
                            <PrintField label="3. Contribution to Scholarship" value={ch.contribution} />
                            <PrintScoreLine label="Score: Contribution" value={sc.contributionScholarship} max={CHAPTER_MAX.contribution} />
                            <PrintField label="4. Clarity & Conciseness" value={ch.clarity} />
                            <PrintScoreLine label="Score: Clarity" value={sc.clarityandConciseness} max={CHAPTER_MAX.clarity} />
                            <PrintField label="5. Grammar & Mechanics" value={ch.grammar} />
                            <PrintScoreLine label="Score: Grammar" value={sc.grammarandMechanics} max={CHAPTER_MAX.grammar} />
                            <PrintField label="6. Style & Tone" value={ch.style} />
                            <PrintScoreLine label="Score: Style" value={sc.styleandTone} max={CHAPTER_MAX.style} />
                            <PrintField label="7. Structure & Organization" value={ch.structure} />
                            <PrintScoreLine label="Score: Structure" value={sc.structureandOrganization} max={CHAPTER_MAX.structure} />
                            <PrintField label="8a. Figures, Tables & Illustrations (relevance, captions, labels)" value={ch.figuresTables} />
                            <PrintField label="8b. Be Specific — which figures / tables / illustrations need adjustment?" value={ch.figuresSpecific} />
                            <PrintScoreLine label="Score: Figures, Tables & Illustrations" value={sc.figuresTablesandIllustrations} max={CHAPTER_MAX.figures} />
                            <PrintField label="9a. Citations & References (chapter linked to bibliography)" value={ch.citationsLink} />
                            <PrintScoreLine label="Score: Citations & References" value={sc.citationsandReferences} max={CHAPTER_MAX.citations} />
                            <PrintField label="9b. References (correctness, completeness)" value={ch.references} />
                            <PrintScoreLine label="Score: References Quality" value={sc.references} max={CHAPTER_MAX.references} />
                        </div>
                    );
                })}
                <div className="review-print-section" style={{ borderTop: '1pt solid #000', paddingTop: '4pt', marginTop: '4pt', fontSize: '9.5pt', display: 'flex', justifyContent: 'space-between', fontWeight: 700 }}>
                    <span>Section C — Averaged Score (capped at 56)</span>
                    <span>{p.chapterCSectionScore} / 56</span>
                </div>
            </div>

            {/* ── SECTION D ── */}
            <div className="review-print-section" style={{ marginBottom: '12pt' }}>
                {sectionHeader('D. Additional Considerations', { value: p.sectionDScore, max: 14 })}
                <PrintField label="1. Market Potential (buyers)" value={p.marketPotential} />
                <PrintScoreLine label="Score: Market Potential" value={p.scoresD1} max={3} />
                <PrintField label="2. Author's Credentials" value={p.authorCredentials} />
                <PrintScoreLine label="Score: Author's Credentials" value={p.scoresD2} max={3} />
                <PrintField label="3a. Pedagogical Features (learning objectives, key terms, etc.)" value={p.pedagogicalFeatures} />
                <PrintField label="3b. Suggested additions / improvements" value={p.pedagogicalSuggestions} />
                <PrintScoreLine label="Score: Pedagogical Features" value={p.scoresD3} max={3} />
                <PrintField label="4. Competition (vs. existing books on the topic)" value={p.competition} />
                <PrintScoreLine label="Score: Competition Analysis" value={p.scoresD4} max={3} />
                <p style={{ fontSize: '8.5pt', fontStyle: 'italic', margin: '4pt 0 2pt 0' }}>
                    5. Manuscript Quality Score — page count: ½ for &lt;251, 1 for 251–350, 1½ for 351–450, 2 for &gt;450.
                </p>
                <PrintScoreLine label="Score: Manuscript Quality" value={p.scoresD5} max={2} />
            </div>

            {/* ── SECTION E ── */}
            <div className="review-print-section" style={{ marginBottom: '12pt' }}>
                {sectionHeader('E. General Comments')}
                <PrintField label="1. Standard — quality of the manuscript" value={p.standardComment} />
                <PrintField label="2. Is it appropriate to have the manuscript published as a book for tertiary education?" value={p.publishable} />
                {p.publishable === 'Not publishable' && (
                    <PrintField label="Reason for 'Not Publishable'" value={p.notPublishableWhy} />
                )}
                <PrintField label="3. General observations" value={p.generalObservations} />
                <PrintField label="4. Specific observations (style, format, foreword, pagination, headings, etc.)" value={p.specificObservations} />
                <PrintField label="5. Final Recommendation & Total Score (specific, unambiguous; pass mark is 60%)" value={p.finalRecommendation} />
            </div>

            {/* ── REVIEWED MANUSCRIPT FILE ── */}
            {p.reviewedManuscriptUrl && (
                <div className="review-print-section" style={{ marginBottom: '12pt', border: '0.75pt solid #000', padding: '6pt 8pt', fontSize: '9pt' }}>
                    <strong>Reviewed Manuscript File on record:</strong> {p.reviewedManuscriptUrl.split('/').pop()}
                </div>
            )}

            {/* ── SIGNATURE ── */}
            <div className="review-print-section" style={{ marginTop: '18pt', paddingTop: '8pt', borderTop: '1pt solid #000' }}>
                <table style={{ width: '100%', fontSize: '9.5pt' }}>
                    <tbody>
                        <tr>
                            <td style={{ verticalAlign: 'top', paddingRight: '12pt', width: '60%' }}>
                                <p style={{ margin: 0 }}>Reviewer's Signature: ____________________________</p>
                                <p style={{ margin: '6pt 0 0 0' }}>Name: {p.ra?.reviewer?.person?.fullName || '____________________________'}</p>
                                <p style={{ margin: '6pt 0 0 0' }}>Date: ____________________________</p>
                            </td>
                            <td style={{ verticalAlign: 'top', borderLeft: '0.5pt solid #999', paddingLeft: '12pt' }}>
                                <p style={{ margin: 0 }}><strong>Total Score:</strong> {p.grandTotal} / 100</p>
                                <p style={{ margin: '4pt 0 0 0' }}><strong>Outcome:</strong> {p.grandTotal >= 60 ? 'PASS' : 'FAIL'}</p>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

        </div>
    );
};

export default ReviewForm;